import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Chart, registerables } from 'chart.js';
import { ChartDataService } from '../../../core/services/api-services/chart-data.service';
import { WageWithdrawalSummary } from './models/WageWithdrawalSummary';
import { Subscription } from 'rxjs';

Chart.register(...registerables);

@Component({
  selector: 'app-weekly-wage-withdrawal-chart',
  standalone: true,
  imports: [DatePipe, DecimalPipe, FormsModule],
  templateUrl: './weekly-wage-withdrawal-chart.component.html',
  styleUrl: './weekly-wage-withdrawal-chart.component.css'
})
export class WeeklyWageWithdrawalChartComponent implements OnInit, OnDestroy {

  @ViewChild('chartCanvas', { static: false }) chartCanvas!: ElementRef<HTMLCanvasElement>;

  chart: Chart | null = null;
  summary: WageWithdrawalSummary | null = null;
  error = '';

  // Date filter properties
  startMonth: string = '';
  endMonth: string = '';
  currentYear: number = new Date().getFullYear();
  currentMonth: number = new Date().getMonth() + 1;

  dataSubscriber$!: Subscription;

  constructor(private chartDateService: ChartDataService) {

    this.dataSubscriber$ = new Subscription();
    // Set default to current year: January to current month
    this.startMonth = `${this.currentYear}-01`;
    this.endMonth = `${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}`;
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    // Load data after view is ready
    this.loadChartData();
  }

  ngOnDestroy(): void {
    this.dataSubscriber$.unsubscribe();
    this.chart?.destroy();
  }

  onFilterApply(): void {
    if (this.startMonth && this.endMonth) {
      const startDate = this.getFirstDayOfMonth(this.startMonth);
      const endDate = this.getLastDayOfMonth(this.endMonth);
      this.loadChartData(startDate, endDate);
    }
  }

  onFilterReset(): void {
    this.startMonth = `${this.currentYear}-01`;
    this.endMonth = `${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}`;
    this.loadChartData();
  }

  private getFirstDayOfMonth(yearMonth: string): string {
    return `${yearMonth}-01`;
  }

  private getLastDayOfMonth(yearMonth: string): string {
    const [year, month] = yearMonth.split('-').map(Number);
    const lastDay = new Date(year, month, 0).getDate();
    return `${yearMonth}-${String(lastDay).padStart(2, '0')}`;
  }

  loadChartData(startDate?: string, endDate?: string): void {
    this.error = '';
    this.dataSubscriber$.add(
      this.chartDateService.getWeeklyChartData(startDate, endDate).subscribe({
        next: (response) => {
          if (response.success) {
            this.summary = response.data.summary;
            this.updateChart(response.data.labels, response.data.data);
          } else {
            // Handle the case where success is false but status is 200
            this.error = response.message || 'No data available for the specified period.';

            // Clear the chart if it exists
            if (this.chart) {
              this.chart.destroy();
              this.chart = null;
            }
          }
        },
        error: (err) => {
          // Extract message from error response
          this.error = err.error?.message || err.message || 'Failed to load chart data';
          console.error(err);

          if (this.chart) {
            this.chart.destroy();
            this.chart = null;
          }
        }
      })
    );
  }

  private updateChart(labels: string[], data: number[]): void {
    if (this.chart) {
      // Update existing chart
      this.chart.data.labels = labels;
      this.chart.data.datasets[0].data = data;
      this.chart.update();
    } else {
      // Create new chart
      this.createChart(labels, data);
    }
  }

  private createChart(labels: string[], data: number[]): void {
    const config: any = {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [{
          label: 'Weekly Wage Withdrawals (LKR)',
          data: data,
          backgroundColor: 'rgba(34, 197, 94, 0.8)',
          borderColor: 'rgba(34, 197, 94, 1)',
          borderWidth: 2,
          borderRadius: 6,
          hoverBackgroundColor: 'rgba(21, 128, 61, 0.9)'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          title: {
            display: true,
            text: 'Weekly Wage Withdrawals',
            font: { size: 18, weight: 'bold' },
            padding: { bottom: 20 }
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: 12,
            titleFont: { size: 14 },
            bodyFont: { size: 13 },
            callbacks: {
              label: (context: any) => {
                return 'Amount: LKR ' + context.parsed.y.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                });
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: (value: any) => 'LKR ' + value.toLocaleString(),
              font: { size: 12 }
            },
            grid: { color: 'rgba(0, 0, 0, 0.05)' }
          },
          x: {
            ticks: { font: { size: 12 } },
            grid: { display: false }
          }
        }
      }
    };

    this.chart = new Chart(this.chartCanvas.nativeElement, config);
  }

  // Optional: Refresh with custom date range
  refreshChart(startDate?: string, endDate?: string): void {
    this.loadChartData(startDate, endDate);
  }
}
